import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  HomeWork as HomeworkIcon,
  Settings as SettingsIcon,
  HelpOutline as EnquiryIcon,
  DirectionsBus as TransportIcon,
  Hotel as HostelIcon,
  MenuBook as LibraryIcon,
  Inventory as StockIcon,
  AccountBalance as AccountsIcon,
  Message as MessagingIcon,
  Assignment as TasksIcon,
  PersonPin as VisitorIcon,
  Badge as IdentityCardIcon,
  PhotoLibrary as MediaIcon,
  Description as CertificateIcon,
  Assessment as ReportsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

/**
 * Menu Item Interface
 * Sidebar visibility is driven by MODULES, not permissions
 */
export interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  moduleCode?: string;        // 👈 SCHOOL MODULE CODE
  logModule?: string;         // 👈 Logging Module Code (if different or no moduleCode)
  requiredRoles?: string[];  // 👈 Role visibility only
  viewPermission?: string;   // 👈 Permission required for visibility (if not ADMIN)
  children?: MenuItem[];     // 👈 Nested menu items
}

/**
 * GLOBAL Scope Menu Items (SuperAdmin only)
 */
export const globalMenuItems: MenuItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    logModule: 'DASHBOARD',
  },
  {
    path: '/superadmin/schools',
    label: 'Schools',
    icon: <SchoolIcon />,
    requiredRoles: ['SUPER_ADMIN'],
    logModule: 'SUPER_ADMIN',
  },
  {
    path: '/modules',
    label: 'Module Assignment',
    icon: <SettingsIcon />,
    requiredRoles: ['SUPER_ADMIN'],
    logModule: 'SUPER_ADMIN',
  },
];

/**
 * SCHOOL Scope Menu Items
 * Visibility controlled by school_modules
 */
export const schoolMenuItems: MenuItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    logModule: 'DASHBOARD',
  },
  {
    path: '/system-settings',
    label: 'System Settings',
    icon: <SettingsIcon />,
    moduleCode: 'SYSTEM_SETTINGS',
    viewPermission: 'SYSTEM_SETTINGS_VIEW',
  },
  {
    path: '/scholar-register',
    label: 'Scholar Register',
    icon: <PersonIcon />,
    moduleCode: 'SCHOLAR_REGISTER',
    viewPermission: 'SCHOLAR_REGISTER_VIEW',
    children: [
      { path: '/scholar-register/students', label: 'Students', icon: <PersonIcon /> },
      { path: '/scholar-register/session-record', label: 'Session Record', icon: <SchoolIcon /> },
      { path: '/scholar-register/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
      { path: '/scholar-register/performance', label: 'Student Performance', icon: <AssessmentIcon /> },
      { path: '/scholar-register/grievance', label: 'Grievance / Suggestion', icon: <EnquiryIcon /> },
      { path: '/scholar-register/complaints', label: 'Student Complaints (All)', icon: <EnquiryIcon /> },
      { path: '/scholar-register/rte', label: 'RTE Students', icon: <PersonIcon /> },
      { path: '/scholar-register/bpl', label: 'BPL Students', icon: <PersonIcon /> },
      { path: '/scholar-register/status', label: 'Active / Deactive Students', icon: <PersonIcon /> },
      { path: '/scholar-register/session-transfer', label: 'Session Transfer', icon: <AssignmentIcon />, viewPermission: 'SESSION_TRANSFER_VIEW' },
      { path: '/scholar-register/subject-assign-subjectwise', label: 'Subject Assign (Subject wise)', icon: <AssignmentIcon />, viewPermission: 'SUBJECT_ASSIGN_VIEW' },
      { path: '/scholar-register/subject-assign-student', label: 'Subject Assign (Student wise)', icon: <AssignmentIcon />, viewPermission: 'STUDENT_SUBJECT_VIEW' },
      { path: '/scholar-register/re-study', label: 'Re-Study Students', icon: <SchoolIcon />, viewPermission: 'RE_STUDY_VIEW' },
      { path: '/scholar-register/branch-link', label: 'Student Branch Link', icon: <SchoolIcon />, viewPermission: 'STUDENT_BRANCH_LINK_VIEW' },
      { path: '/scholar-register/house-assign', label: 'House Assign', icon: <HostelIcon />, viewPermission: 'STUDENT_HOUSE_VIEW' },
      { path: '/scholar-register/logs', label: 'Student Log', icon: <AssessmentIcon />, viewPermission: 'STUDENT_LOG_VIEW' },
      { path: '/scholar-register/reports', label: 'Report', icon: <ReportsIcon />, viewPermission: 'STUDENT_REPORT_VIEW' },
      { path: '/scholar-register/certificates', label: 'Issue Certificate', icon: <CertificateIcon />, viewPermission: 'CERTIFICATE_VIEW' },
      { path: '/scholar-register/certificates/id-cards', label: 'Student ID Card', icon: <IdentityCardIcon />, viewPermission: 'ID_CARD_VIEW' },
      { path: '/scholar-register/gate-pass', label: 'Gate Pass', icon: <SecurityIcon />, viewPermission: 'GATE_PASS_VIEW' },
      { path: '/scholar-register/track-history', label: 'Student Track History', icon: <AssessmentIcon />, viewPermission: 'STUDENT_TRACK_HISTORY_VIEW' },
    ],
  },
  {
    path: '/staff',
    label: 'Staff',
    icon: <PersonIcon />,
    moduleCode: 'STAFF',
    viewPermission: 'STAFF_VIEW',
  },
  {
    path: '/attendance',
    label: 'Attendance',
    icon: <AssignmentIcon />,
    moduleCode: 'ATTENDANCE',
    viewPermission: 'ATTENDANCE_VIEW',
  },
  {
    path: '/fees',
    label: 'Fees',
    icon: <PaymentIcon />,
    moduleCode: 'FEE',
    viewPermission: 'FEE_VIEW',
  },
  {
    path: '/examinations',
    label: 'Examinations',
    icon: <AssessmentIcon />,
    moduleCode: 'EXAMINATION',
    viewPermission: 'EXAMINATION_VIEW',
  },
  {
    path: '/homework',
    label: 'Homework',
    icon: <HomeworkIcon />,
    moduleCode: 'HOMEWORK',
    viewPermission: 'HOMEWORK_VIEW',
  },
  {
    path: '/enquiry',
    label: 'Enquiry',
    icon: <EnquiryIcon />,
    moduleCode: 'ENQUIRY',
    viewPermission: 'ENQUIRY_VIEW',
  },
  {
    path: '/transportation',
    label: 'Transportation',
    icon: <TransportIcon />,
    moduleCode: 'TRANSPORTATION',
    viewPermission: 'TRANSPORTATION_VIEW',
  },
  {
    path: '/hostel',
    label: 'Hostel',
    icon: <HostelIcon />,
    moduleCode: 'HOSTEL',
    viewPermission: 'HOSTEL_VIEW',
  },
  {
    path: '/library',
    label: 'Library',
    icon: <LibraryIcon />,
    moduleCode: 'LIBRARY',
    viewPermission: 'LIBRARY_VIEW',
  },
  {
    path: '/stock',
    label: 'Stock',
    icon: <StockIcon />,
    moduleCode: 'STOCK',
    viewPermission: 'STOCK_VIEW',
  },
  {
    path: '/accounts',
    label: 'Accounts',
    icon: <AccountsIcon />,
    moduleCode: 'ACCOUNTS',
    viewPermission: 'ACCOUNTS_VIEW',
  },
  {
    path: '/messaging',
    label: 'Messaging',
    icon: <MessagingIcon />,
    moduleCode: 'MESSAGING',
    viewPermission: 'MESSAGING_VIEW',
  },
  {
    path: '/tasks',
    label: 'Tasks',
    icon: <TasksIcon />,
    moduleCode: 'TASKS',
    viewPermission: 'TASKS_VIEW',
  },
  {
    path: '/visitors',
    label: 'Visitors',
    icon: <VisitorIcon />,
    moduleCode: 'VISITORS',
    viewPermission: 'VISITORS_VIEW',
  },
  {
    path: '/identity-cards',
    label: 'Identity Cards',
    icon: <IdentityCardIcon />,
    moduleCode: 'IDENTITY_CARDS',
    viewPermission: 'IDENTITY_CARDS_VIEW',
  },
  {
    path: '/media',
    label: 'Pictures and Videos',
    icon: <MediaIcon />,
    moduleCode: 'MEDIA',
    viewPermission: 'MEDIA_VIEW',
  },
  {
    path: '/certificates',
    label: 'Certificates',
    icon: <CertificateIcon />,
    moduleCode: 'CERTIFICATES',
    viewPermission: 'CERTIFICATES_VIEW',
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: <ReportsIcon />,
    moduleCode: 'REPORTS',
    viewPermission: 'REPORTS_VIEW',
  },
];

/**
 * Menu selector by role
 */
export const getMenuItems = (userRole?: string): MenuItem[] => {
  if (userRole === 'SUPER_ADMIN') {
    return globalMenuItems;
  }
  return schoolMenuItems;
};
